import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import processEvent from 'analytics/processEvent';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import { sendSOTLinkTrackingClickEvent } from 'analytics/utils/sotTracking';
import ErrorsActions from 'actions/ErrorsActions';
import ErrorConstants from 'utils/ErrorConstants';
import store from 'store/Store';
import orderUtils from 'utils/Order';
import { BASKET_TYPES } from 'constants/RwdBasket';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';

const { hasHalAddress } = orderUtils;

// Returns 1, 2, or 3 based on which Fast Delivery methods are available at the user's zip.
// Returns null when there is no SameDay shipping group (no Fast Delivery items in basket).
function getFastDeliveryAvailableValue() {
    const orderDetails = store.getState().order?.orderDetails;

    if (!orderDetails) {
        return null;
    }

    const sameDayShippingGroup = orderUtils.getSameDayShippingGroup(orderDetails);

    if (!sameDayShippingGroup) {
        return null;
    }

    const availableMethods = sameDayShippingGroup.availableShippingMethods || [];
    // 'Same Day' matches both 'Same Day Delivery' and 'Free Rouge Same Day Delivery'
    const isSddAvailable = availableMethods.some(m => m.shippingMethodType?.includes('Same Day'));
    // NDD shippingMethodType from API contains 'Next Day'
    const isNddAvailable = availableMethods.some(m => m.shippingMethodType?.includes('Next Day'));

    if (isSddAvailable && isNddAvailable) {
        return 3;
    }

    if (isNddAvailable) {
        return 2;
    }

    if (isSddAvailable) {
        return 1;
    }

    return null;
}

// Builds s.products string with event323 bound only to Fast Delivery SKUs.
// Get It Shipped SKUs appear in the string with eVar26 only (no event323 per spec).
function buildFastDeliveryAvailableProductString(availableValue) {
    const basket = store.getState().page?.rwdBasket?.basket;
    const parts = [];

    (basket?.itemsByBasket || []).forEach(basketGroup => {
        const isFastDelivery = basketGroup.basketType === BASKET_TYPES.SAMEDAY_BASKET;

        (basketGroup.items || []).forEach(item => {
            const skuId = item.sku?.skuId;

            if (!skuId) {
                return;
            }

            if (isFastDelivery) {
                parts.push(`;${skuId};;;;event323=${availableValue};eVar26=${skuId}`);
            } else {
                parts.push(`;${skuId};;;;eVar26=${skuId}`);
            }
        });
    });

    return parts.join(',');
}

export default (function () {
    const ERROR_LEVEL = ErrorConstants.ERROR_LEVEL;

    const methods = {
        setRopisData: function () {
            digitalData.page.pageInfo.pageName = anaConsts.PAGE_NAMES.ROPIS_CHECKOUT;
        },

        setPageName: function (focus, checkoutPath) {
            // Always use the URL-based pageName for initial PAGE_LOAD.
            // Section-specific tracking (e.g., 'shipping-add address') is handled
            // by ASYNC_PAGE_LOAD calls from section components like ShipAddressSection.
            digitalData.page.pageInfo.pageName = analyticsUtils.convertName(checkoutPath);
        },

        setPageType: () => {
            digitalData.page.category.pageType = anaConsts.PAGE_TYPES.CHECKOUT;
        },

        processAsyncPageLoad: function (checkoutPath, pageTypeString = anaConsts.PAGE_TYPES.CHECKOUT) {
            const pageDetail = analyticsUtils.convertName(checkoutPath);
            const name = `${pageTypeString}:${pageDetail}:n/a:*`;

            const eventStrings = [anaConsts.Event.SC_CHECKOUT];

            if (hasHalAddress()) {
                eventStrings.push(anaConsts.Event.EVENT_247);
            }

            const eventData = {
                pageName: name,
                previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName,
                eventStrings,
                pageType: pageTypeString,
                pageDetail
            };

            // AC1: Fast Delivery Available (event323) — place order step only
            if (DeliveryOptionsUtils.isNDDEnabled() && pageDetail === anaConsts.PAGE_NAMES.PLACE_ORDER) {
                const fastDeliveryAvailableValue = getFastDeliveryAvailableValue();

                if (fastDeliveryAvailableValue !== null) {
                    eventStrings.push(`${anaConsts.Event.EVENT_323}=${fastDeliveryAvailableValue}`);
                    eventData.productStrings = buildFastDeliveryAvailableProductString(fastDeliveryAvailableValue);
                }
            }

            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, { data: eventData });

            // Update page name for subsequent calls
            digitalData.page.attributes.sephoraPageInfo.pageName = name;
            digitalData.page.pageInfo.pageName = pageDetail;
            analyticsUtils.setNextPageData({ pageName: name });
        },

        watchForErrors: () => {
            //Parse through the error object to get what we need in the format we need it in
            const getAnalyticsFields = (errors = {}) => {
                const formErrors = {
                    errorFields: [],
                    errorMessages: []
                };
                Object.keys(ERROR_LEVEL).forEach(errorLevel => {
                    Object.keys(errors[errorLevel]).forEach(errorKey => {
                        formErrors.errorFields.push(errorKey);
                        formErrors.errorMessages.push(errors[errorLevel][errorKey].message);
                    });
                });

                return formErrors;
            };

            const handleErrors = errors => {
                const analyticsFields = getAnalyticsFields(errors.errors);

                // Correctly set pageDetail based on most recent asyncPageLoad. If there are no
                // asyncPageLoad events, it will default to pageDetail of initial pageLoad event
                const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
                let pageDetail = '';
                let previousPageName = digitalData.page.attributes.previousPageData.pageName;

                if (mostRecentAsyncLoadEvent) {
                    pageDetail = mostRecentAsyncLoadEvent.eventInfo.attributes.pageDetail;
                    previousPageName = mostRecentAsyncLoadEvent.eventInfo.attributes.previousPageName;
                }

                // Promo errors are handled elsewhere and have different requirements than
                // general checkout errors
                const isPromoError = analyticsFields.errorFields.length && analyticsFields.errorFields[0] === 'basketLevelMsg';

                if ((analyticsFields.errorMessages.length || analyticsFields.errorFields.length) && !isPromoError) {
                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            bindingMethods: linkTrackingError,
                            errorMessages: analyticsFields.errorMessages,
                            fieldErrors: analyticsFields.errorFields,
                            eventStrings: [anaConsts.Event.EVENT_71],
                            linkName: anaConsts.EVENT_NAMES.ERROR,
                            previousPage: previousPageName,
                            usePreviousPageName: true,
                            pageDetail: pageDetail,
                            pageName: digitalData.page.attributes.sephoraPageInfo.pageName
                        }
                    });
                }
            };

            store.watchAction(ErrorsActions.TYPES.VALIDATE_ERRORS, handleErrors);
        },

        triggerSOTAnalytics: ({
            eventName, sotType, pageName, origin, paymentMethod, ...data
        }) => {
            sendSOTLinkTrackingClickEvent({
                eventData: {
                    linkName: eventName,
                    actionInfo: eventName,
                    specificEventName: eventName,
                    sotType,
                    pageName,
                    origin,
                    paymentMethod,
                    ...data
                }
            });
        },

        sotPaymentOptionCheckout: paymentMethod => {
            methods.triggerSOTAnalytics({
                eventName: anaConsts.EVENT_NAMES.EXPRESS_PAYMENT_SELECTION,
                sotType: anaConsts.EVENT_NAMES.EXPRESS_PAYMENT_SELECTION,
                pageName: anaConsts.CONTEXT.CHECKOUT_PAGE,
                origin: anaConsts.EVENT_NAMES.PAYPAL_EXPRESS.PAGE_DETAIL.EXPRESS_CHECKOUT,
                paymentMethod
            });
        }
    };

    return methods;
}());
